import { debounce } from "lodash";
import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { listPageProps, setPageProp as setPagePropApi, deletePageProp as deletePagePropApi } from "../../api/page/pageProp";
import { useAppId } from "../../hook";
import { ActionType } from "../../actionTypes";
import { AppDispatch, RootState } from "../../reducer";
import { PagePropTypeText } from "../../types/page/pageProp";

export const useFetchPageProps = (pageId: string | undefined): Record<string, { value: string; "prop-type": string; options?: Array<{ label: string }> }> => {
  const [appId] = useAppId();
  const dispatch = useDispatch<AppDispatch>();

  const props = useSelector((state: RootState) =>
    pageId ? (state.ui as any).pageProps?.[pageId] as Record<string, { value: string; "prop-type": string; options?: Array<{ label: string }> }> | undefined : undefined,
  );

  useEffect(() => {
    if (!appId || !pageId) return;
    listPageProps(appId, pageId).then((result) => {
      dispatch({ type: ActionType.SET_PAGE_PROPS, pageId, props: result });
    });
  }, [appId, pageId]);

  return props ?? {};
};

export const useSetPageProp = (pageId: string) => {
  const [appId] = useAppId();
  const dispatch = useDispatch<AppDispatch>();

  const setProp = useMemo(
    () =>
      debounce(async (name: string, value: string, propType: string = PagePropTypeText, options: Array<{ label: string }> = []) => {
        if (!appId) return;
        const updated = await setPagePropApi(appId, pageId, name, value, propType, options);
        dispatch({ type: ActionType.SET_PAGE_PROPS, pageId, props: updated });
      }, 250),
    [appId, pageId],
  );

  return setProp;
};

export const useFetchPagePropsCallback = () => {
  const [appId] = useAppId();
  const dispatch = useDispatch<AppDispatch>();

  return async (pageId: string) => {
    if (!appId || !pageId) return;
    const result = await listPageProps(appId, pageId);
    dispatch({ type: ActionType.SET_PAGE_PROPS, pageId, props: result });
  };
};

export const useDeletePageProp = (pageId: string) => {
  const [appId] = useAppId();
  const dispatch = useDispatch<AppDispatch>();

  return async (name: string) => {
    if (!appId) return;
    const updated = await deletePagePropApi(appId, pageId, name);
    dispatch({ type: ActionType.SET_PAGE_PROPS, pageId, props: updated });
  };
};
